import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useFeatures } from '@xingternal/hub';
import { ProfileImage } from '@xingternal/profile-image';
import { ProfileInfo } from '@xingternal/profile-info';
import { BodyCopy, Headline, Meta } from '@xingternal/typography';

import { ScrollTrackingModuleWrapper } from '@/components/job-posting/components/scroll-tracking-module-wrapper';
import { HRContact } from '@/domain/job-details/components/hr-contact';
import { useCompanyRange } from '@/domain/job-details/hooks';
import { JobLocation } from '@/graphql/generated';
import { appendQueryParams } from '@/helpers/query-string';
import { redirectUrl } from '@/helpers/redirect-url';
import { useBreakpoints } from '@/hooks';
import { ConnectedContact } from '@/types';

import { EmployerActions } from './employer-actions';
import { NonNullCompanyInfo } from './employer-actions/employer-types';

import * as S from './company-module.styles';

type Props = {
  companyInfo: NonNullCompanyInfo;
  location: JobLocation;
  contact?: ConnectedContact;
  industry?: string;
  companyId?: string;
};

export const ModuleTitle: React.FC<{ titleLabel: string }> = ({ titleLabel }) => (
  <S.ModuleTitle size="xlarge" sizeWide="xxlarge">
    <FormattedMessage id={titleLabel} />
  </S.ModuleTitle>
);

export const CompanyModule: React.FC<Props> = ({ companyInfo, location, contact, industry, companyId = '' }) => {
  const { data } = useFeatures();
  const isPremium = data?.isPremium;
  const { isDesktop, isMobile, isTablet } = useBreakpoints();
  const showHRModule = isPremium && contact;

  const { company, companyNameOverride } = companyInfo;
  const logo = company?.logo?.url as string;
  const companySizeRange = company?.companySizeRange;
  const range = useCompanyRange(companySizeRange);
  const localizedCountry = location?.country?.localizationValue;
  const publicLink = company?.links?.public || '';

  const companyModuleAlignment = (isDesktop && showHRModule) || isMobile ? 'vertical' : 'horizontal';
  const hrContactAlignment = isTablet ? 'horizontal' : 'vertical';
  const actionsAlignment = isMobile ? 'vertical' : 'horizontal';

  const companyURLWithTracking =
    (publicLink && appendQueryParams(publicLink, { sc_o: 'jobs_company_module_logo_name_click' })) ?? '';

  const handleCompanyRedirect = (event: React.MouseEvent<HTMLElement>): void =>
    redirectUrl(event, companyURLWithTracking);

  return (
    <ScrollTrackingModuleWrapper moduleTrackingName="Company">
      <S.StyledJobPostingModule testId="employer-info-module">
        <ModuleTitle titleLabel="JOB_POSTING_ABOUT_COMPANY_H" />
        <S.AboutCompanyWrapper>
          <S.CompanyModuleWrapper>
            <S.CompanyModuleStyled
              onClick={(e: React.MouseEvent<HTMLElement>) => handleCompanyRedirect(e)}
              className="employer-company"
            >
              <ProfileInfo
                alignment={companyModuleAlignment}
                profileImage={
                  <ProfileImage alt="company logo" profileName={companyNameOverride} size="medium" src={logo} />
                }
                actions={<EmployerActions company={company} alignment={actionsAlignment} />}
                data-testid={`company-info-${companyModuleAlignment}`}
              >
                <Headline size="large">{companyNameOverride}</Headline>
                {industry ? <BodyCopy size="medium">{industry}</BodyCopy> : null}
                {companySizeRange ? (
                  <BodyCopy size="medium">
                    {range}&nbsp;
                    <FormattedMessage id="EP_EMPLOYEES" />
                  </BodyCopy>
                ) : null}
                <Meta size="small">
                  {location?.city}
                  {localizedCountry ? `, ${localizedCountry}` : null}
                </Meta>
              </ProfileInfo>
            </S.CompanyModuleStyled>
          </S.CompanyModuleWrapper>
          {showHRModule ? (
            <S.HRContactWrapper>
              <HRContact
                contact={contact}
                alignment={hrContactAlignment}
                showTitle={!isDesktop}
                companyId={companyId}
                className="employer-hr-contact"
              />
            </S.HRContactWrapper>
          ) : null}
        </S.AboutCompanyWrapper>
      </S.StyledJobPostingModule>
    </ScrollTrackingModuleWrapper>
  );
};
